// IIFE (Immediately Invoked Function Expression)
(function () {
  'use strict';

  // Base URL for all BotMe resources - shared across all instances
  const BOTME_BASE_URL = "https://botme-ai.com";
  const BOTME_CHAT_APP_ORIGIN = "https://custom-ai-agent-chat.web.app";
  // These are BotMe-controlled iframe app origins, not customer host-page origins.
  // The plugin can run on any client domain; this allowlist only validates postMessage
  // events coming back from the managed iframe as it navigates between BotMe origins.
  const BOTME_MANAGED_CHAT_ORIGINS = [
    BOTME_BASE_URL,
    BOTME_CHAT_APP_ORIGIN
  ];

  // Animation timing constants (in milliseconds)
  const ANIM_INITIAL_DELAY = 10;           // Initial delay before starting animations
  const ANIM_SCALE_DURATION = 300;         // Duration to wait for scale animations to complete
  const ANIM_AUTO_OPEN_DELAY = 500;        // Delay before auto-opening chat
  const ANIM_FAB_INIT_DELAY = 100;         // Delay before initializing FAB button
  const ATTACH_TRIGGERS_DELAY = 1000;         // Delay before initializing FAB button
  const ANIM_BUTTON_STAGGER = 50;          // Delay between sequential button animations

  // CSS transition durations
  const TRANSITION_TRANSFORM = "0.3s ease-out";
  const TRANSITION_OPACITY = "0.2s ease-out";
  const TRANSITION_FAST = "0.2s";

  // Scale transform values
  const SCALE_HIDDEN = "scale(0)";
  const SCALE_VISIBLE = "scale(1)";

  // Opacity values
  const OPACITY_HIDDEN = "0";
  const OPACITY_VISIBLE = "1";

  function BotMePlugin(config) {
    function normalizeBotIdValue(value) {
      if (typeof value !== "string") {
        return null;
      }

      const normalizedValue = value.trim();
      return normalizedValue || null;
    }

    function resolveBotId(source) {
      if (!source) {
        return null;
      }

      if (typeof source === "string") {
        return normalizeBotIdValue(source);
      }

      if (typeof source !== "object") {
        return null;
      }

      return normalizeBotIdValue(source.botId) || normalizeBotIdValue(source.apiKey);
    }

    function normalizeConfigObject(source) {
      if (!source || typeof source !== "object") {
        return source;
      }

      const resolvedBotId = resolveBotId(source);
      if (!resolvedBotId) {
        if (typeof source.botId === "string" && !source.botId.trim()) {
          source.botId = null;
        }

        if (typeof source.apiKey === "string" && !source.apiKey.trim()) {
          source.apiKey = null;
        }

        return source;
      }

      if (source.botId !== resolvedBotId) {
        source.botId = resolvedBotId;
      }

      if (source.apiKey !== resolvedBotId) {
        source.apiKey = resolvedBotId;
      }

      return source;
    }

    // Generate unique instance ID
    this.instanceId = 'botme-' + Math.random().toString(36).substr(2, 9);

    // Check if debug logging should be enabled (only for file:/// URLs or localhost)
    this.debugEnabled = window.location.protocol === 'file:' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    // Canonical public config is window.botMeConfig.botId.
    // Keep legacy aliases as fallbacks so existing embeds do not break.
    normalizeConfigObject(window.botMeConfig);
    normalizeConfigObject(window.agentChatConfig);

    const globalConfig = window.botMeConfig || window.agentChatConfig || {};
    const defaultBotId = resolveBotId(globalConfig) ||
      window.botMeApiKey ||
      window.agentChatAgentKey ||
      window.aiChatAgentKey;

    // Default configuration
    const defaults = {
      botId: defaultBotId,
      apiKey: defaultBotId,
      buttonIcon: BOTME_BASE_URL + "/img/logo.png",
      buttonTitle: "AI Agent",
      buttonSize: "40px",
      buttonPosition: {
        bottom: "50px",
        right: "50px"
      },
      buttonZIndex: 99,
      autoPlacement: true,
      autoOpen: false,
      displayMode: "modal", // "modal" | "drawer-left" | "drawer-right" | "fullscreen-popup"
      modalDragAndResize: true,
      chatDimensions: {
        desktop: {
          width: "400px",
          height: "80vh",
          maxHeight: "80vh"
        },
        mobile: {
          width: "90vw",
          height: "70vh",
          maxHeight: "70vh"
        }
      },
      chatPosition: {
        bottom: "20px",
        right: "20px"
      },
      chatBorderRadius: "12px",
      chatBoxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
      chatZIndex: 999999,
      chatBackgroundColor: "ffffff",
      chatHeaderColor: "6b7280",
      chatTitle: null,
      overlayBackground: "rgba(0, 0, 0, 0.5)",
      overlayZIndex: 999998,
      closeButtonIcon: BOTME_BASE_URL + "/svg/close-button-white-bg.svg",
      closeButtonSize: "32px",
      closeButtonZIndex: 1000000,
      minimizeButtonIcon: BOTME_BASE_URL + "/svg/minimize-button-white-bg.svg",
      minimizeButtonSize: "32px",
      menuButtonIcon: BOTME_BASE_URL + "/svg/menu-button-white-bg.svg",
      menuButtonSize: "32px",
      disableEscapeKey: false,
      dynamicAnchor: false,
      themeMode: null, // "light" | "dark" | null
      interfaceLanguageCode: null,
      logIframeMessages: false,
      iframeMessageAllowedOrigins: BOTME_MANAGED_CHAT_ORIGINS.slice(),
      onChatCallback: null
    };

    // Priority: passed config > global config object > defaults
    // First merge defaults with global config
    const mergedWithGlobal = Object.assign({}, defaults, globalConfig);
    // Then merge with passed config (highest priority)
    this.config = Object.assign({}, mergedWithGlobal, config || {});

    // Deep merge for nested objects
    const finalConfig = config || {};
    const normalizedBotId = resolveBotId(this.config);
    if (normalizedBotId) {
      this.config.botId = normalizedBotId;
      this.config.apiKey = normalizedBotId;
    }

    if (globalConfig.buttonPosition || finalConfig.buttonPosition) {
      this.config.buttonPosition = Object.assign(
        {},
        defaults.buttonPosition,
        globalConfig.buttonPosition || {},
        finalConfig.buttonPosition || {}
      );
    }

    if (globalConfig.chatDimensions || finalConfig.chatDimensions) {
      this.config.chatDimensions = {
        desktop: Object.assign(
          {},
          defaults.chatDimensions.desktop,
          (globalConfig.chatDimensions && globalConfig.chatDimensions.desktop) || {},
          (finalConfig.chatDimensions && finalConfig.chatDimensions.desktop) || {}
        ),
        mobile: Object.assign(
          {},
          defaults.chatDimensions.mobile,
          (globalConfig.chatDimensions && globalConfig.chatDimensions.mobile) || {},
          (finalConfig.chatDimensions && finalConfig.chatDimensions.mobile) || {}
        )
      };
    }

    if (globalConfig.chatPosition || finalConfig.chatPosition) {
      this.config.chatPosition = Object.assign(
        {},
        defaults.chatPosition,
        globalConfig.chatPosition || {},
        finalConfig.chatPosition || {}
      );
    }

    this.isOpened = false;
    this.isMinimized = false;
    this.isListeningToEscapeKeyPress = false;
    this.isInfoPopupOpen = false;
    this.lastClickX = null;
    this.lastClickY = null;
    this.debugLogs = [];
    this.usedDynamicPositioning = false; // Track if current chat session used dynamic positioning
    this.isListeningToIframeMessages = false;
    this.updateButtonsBound = null;
    this.resolveBotId = resolveBotId;
    this.pendingIframeContext = null;
    this.imageViewerState = {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      dragOriginX: 0,
      dragOriginY: 0,
      currentImageUrl: null,
      objectUrl: null,
      requestId: null,
      expiryTimer: null
    };

    this.getConfiguredBotId = function () {
      return this.resolveBotId(this.config);
    };

    // Logger function - only logs if debug is enabled
    this.log = function (event, data) {
      if (!this.debugEnabled) return;

      this.debugLogs.push({
        timestamp: new Date().toISOString(),
        instanceId: this.instanceId,
        event: event,
        ...data
      });
    };

    // Helper function to generate unique element IDs for this instance
    this.getElementId = function (elementName) {
      return this.instanceId + '-' + elementName;
    };

    // Helper function to retrieve elements by name (replaces querySelector pattern)
    this.getPluginElement = function (elementName) {
      return document.querySelector("#" + this.getElementId(elementName));
    };

    this.normalizeThemeMode = function (mode) {
      if (typeof mode !== "string") {
        return null;
      }

      var normalized = mode.toLowerCase();
      if (normalized === "dark" || normalized === "light") {
        return normalized;
      }

      return null;
    };

    this.getThemeMode = function () {
      return this.normalizeThemeMode(this.currentThemeMode || this.config.themeMode);
    };

    this.resolveInterfaceLanguageCode = function () {
      if (typeof this.config.interfaceLanguageCode === "string" && this.config.interfaceLanguageCode.trim()) {
        return this.config.interfaceLanguageCode.trim().toLowerCase().split("-")[0];
      }

      if (typeof navigator.language === "string" && navigator.language.trim()) {
        return navigator.language.trim().toLowerCase().split("-")[0];
      }

      return null;
    };

    this.setThemeMode = function (mode) {
      var normalized = this.normalizeThemeMode(mode);
      if (!normalized) {
        return;
      }

      this.currentThemeMode = normalized;
      this.config.themeMode = normalized;

      var iframe = this.getPluginElement("corner-chat-element");
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: "botme:setThemeMode",
            themeMode: normalized,
            mode: normalized
          },
          "*"
        );
      }

      var toggleItem = this.getPluginElement("corner-chat-theme-toggle");
      if (toggleItem) {
        toggleItem.textContent = normalized === "dark"
          ? "Switch to light mode"
          : "Switch to dark mode";
      }

      this.log("theme_mode_updated", {
        mode: normalized
      });
    };

    this.clampImageViewerScale = function (value) {
      return Math.min(Math.max(value, 1), 5);
    };

    this.updateImageViewerTransform = function () {
      var image = this.getPluginElement("fullscreen-image-viewer-image");
      if (!image) {
        return;
      }

      image.style.transform = "translate(" + this.imageViewerState.offsetX + "px, " + this.imageViewerState.offsetY + "px) scale(" + this.imageViewerState.scale + ")";
      image.style.cursor = this.imageViewerState.scale > 1 ?
        (this.imageViewerState.isDragging ? "grabbing" : "grab") :
        "zoom-in";
    };

    this.resetImageViewer = function () {
      this.imageViewerState.scale = 1;
      this.imageViewerState.offsetX = 0;
      this.imageViewerState.offsetY = 0;
      this.imageViewerState.isDragging = false;
      this.imageViewerState.dragOriginX = 0;
      this.imageViewerState.dragOriginY = 0;
      this.updateImageViewerTransform();
    };

    this.setImageViewerScale = function (nextScale) {
      this.imageViewerState.scale = this.clampImageViewerScale(nextScale);
      if (this.imageViewerState.scale === 1) {
        this.imageViewerState.offsetX = 0;
        this.imageViewerState.offsetY = 0;
      }
      this.updateImageViewerTransform();
    };

    this.closeImageViewer = function () {
      var overlay = this.getPluginElement("fullscreen-image-viewer-overlay");
      if (overlay) {
        overlay.style.display = "none";
      }

      var image = this.getPluginElement("fullscreen-image-viewer-image");
      var openLink = this.getPluginElement("fullscreen-image-viewer-open-link");
      if (image) image.removeAttribute("src");
      if (openLink) openLink.removeAttribute("href");
      clearTimeout(this.imageViewerState.expiryTimer);
      if (this.imageViewerState.objectUrl) URL.revokeObjectURL(this.imageViewerState.objectUrl);
      this.imageViewerState.objectUrl = null;
      this.imageViewerState.requestId = null;
      this.imageViewerState.expiryTimer = null;
      this.imageViewerState.currentImageUrl = null;
      this.resetImageViewer();
    };

    this.ensureImageViewer = function () {
      var existingOverlay = this.getPluginElement("fullscreen-image-viewer-overlay");
      if (existingOverlay) {
        return existingOverlay;
      }

      var overlay = document.createElement("div");
      overlay.id = this.getElementId("fullscreen-image-viewer-overlay");
      overlay.style.position = "fixed";
      overlay.style.inset = "0";
      overlay.style.display = "none";
      overlay.style.flexDirection = "column";
      overlay.style.backgroundColor = "rgba(2, 6, 23, 0.92)";
      overlay.style.zIndex = String((this.config.chatZIndex || 999999) + 10);

      var header = document.createElement("div");
      header.style.display = "flex";
      header.style.alignItems = "center";
      header.style.justifyContent = "space-between";
      header.style.gap = "12px";
      header.style.padding = "12px 16px";
      header.style.borderBottom = "1px solid rgba(255, 255, 255, 0.08)";

      var titleWrap = document.createElement("div");
      var title = document.createElement("div");
      title.textContent = "Image viewer";
      title.style.color = "#ffffff";
      title.style.fontSize = "14px";
      title.style.fontWeight = "600";
      var subtitle = document.createElement("div");
      subtitle.textContent = "Zoom with buttons or wheel. Drag when zoomed in.";
      subtitle.style.color = "rgba(255, 255, 255, 0.68)";
      subtitle.style.fontSize = "12px";
      subtitle.style.marginTop = "2px";
      titleWrap.appendChild(title);
      titleWrap.appendChild(subtitle);

      var controls = document.createElement("div");
      controls.style.display = "flex";
      controls.style.alignItems = "center";
      controls.style.gap = "8px";

      var createControlButton = function (label, onClick) {
        var button = document.createElement("button");
        button.type = "button";
        button.textContent = label;
        button.style.width = "38px";
        button.style.height = "38px";
        button.style.borderRadius = "999px";
        button.style.border = "1px solid rgba(255, 255, 255, 0.18)";
        button.style.backgroundColor = "rgba(17, 24, 39, 0.65)";
        button.style.backdropFilter = "blur(10px)";
        button.style.color = "#ffffff";
        button.style.cursor = "pointer";
        button.style.fontSize = "16px";
        button.onclick = function (event) {
          event.preventDefault();
          event.stopPropagation();
          onClick();
        };
        return button;
      };

      var createControlLink = function (label, elementId) {
        var link = document.createElement("a");
        link.id = this.getElementId(elementId);
        link.href = "#";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = label;
        link.style.display = "inline-flex";
        link.style.alignItems = "center";
        link.style.justifyContent = "center";
        link.style.width = "38px";
        link.style.height = "38px";
        link.style.borderRadius = "999px";
        link.style.border = "1px solid rgba(255, 255, 255, 0.18)";
        link.style.backgroundColor = "rgba(17, 24, 39, 0.65)";
        link.style.backdropFilter = "blur(10px)";
        link.style.color = "#ffffff";
        link.style.cursor = "pointer";
        link.style.fontSize = "16px";
        link.style.textDecoration = "none";
        link.onclick = function (event) {
          event.stopPropagation();
          if (!this.imageViewerState.currentImageUrl) {
            event.preventDefault();
          }
        }.bind(this);
        return link;
      }.bind(this);

      controls.appendChild(createControlButton("−", () => this.setImageViewerScale(this.imageViewerState.scale - 0.4)));
      controls.appendChild(createControlButton("+", () => this.setImageViewerScale(this.imageViewerState.scale + 0.4)));
      controls.appendChild(createControlButton("↺", () => this.resetImageViewer()));
      controls.appendChild(createControlLink("↓", "fullscreen-image-viewer-open-link"));
      controls.appendChild(createControlButton("×", () => this.closeImageViewer()));

      header.appendChild(titleWrap);
      header.appendChild(controls);

      var stage = document.createElement("div");
      stage.id = this.getElementId("fullscreen-image-viewer-stage");
      stage.style.flex = "1";
      stage.style.display = "flex";
      stage.style.alignItems = "center";
      stage.style.justifyContent = "center";
      stage.style.overflow = "hidden";
      stage.style.position = "relative";

      var image = document.createElement("img");
      image.id = this.getElementId("fullscreen-image-viewer-image");
      image.alt = "Chat image";
      image.draggable = false;
      image.style.maxWidth = "min(94vw, 1100px)";
      image.style.maxHeight = "calc(100vh - 120px)";
      image.style.width = "auto";
      image.style.height = "auto";
      image.style.objectFit = "contain";
      image.style.borderRadius = "12px";
      image.style.boxShadow = "0 30px 80px rgba(0, 0, 0, 0.45)";
      image.style.userSelect = "none";
      image.style.webkitUserSelect = "none";
      image.style.transition = "transform 0.18s ease";
      image.style.transformOrigin = "center center";

      image.addEventListener("mousedown", (event) => {
        if (this.imageViewerState.scale <= 1) {
          return;
        }

        event.preventDefault();
        this.imageViewerState.isDragging = true;
        this.imageViewerState.dragOriginX = event.clientX - this.imageViewerState.offsetX;
        this.imageViewerState.dragOriginY = event.clientY - this.imageViewerState.offsetY;
        this.updateImageViewerTransform();
      });

      image.addEventListener("dblclick", () => {
        if (this.imageViewerState.scale > 1) {
          this.resetImageViewer();
        } else {
          this.setImageViewerScale(2.5);
        }
      });

      image.addEventListener("wheel", (event) => {
        event.preventDefault();
        this.setImageViewerScale(this.imageViewerState.scale + (event.deltaY < 0 ? 0.25 : -0.25));
      }, { passive: false });

      stage.addEventListener("mousemove", (event) => {
        if (!this.imageViewerState.isDragging) {
          return;
        }

        this.imageViewerState.offsetX = event.clientX - this.imageViewerState.dragOriginX;
        this.imageViewerState.offsetY = event.clientY - this.imageViewerState.dragOriginY;
        this.updateImageViewerTransform();
      });

      var stopDragging = () => {
        if (!this.imageViewerState.isDragging) {
          return;
        }

        this.imageViewerState.isDragging = false;
        this.updateImageViewerTransform();
      };

      stage.addEventListener("mouseup", stopDragging);
      stage.addEventListener("mouseleave", stopDragging);
      overlay.addEventListener("click", (event) => {
        if (event.target === overlay) {
          this.closeImageViewer();
        }
      });

      window.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.getPluginElement("fullscreen-image-viewer-overlay")?.style.display === "flex") {
          this.closeImageViewer();
        }
      });

      stage.appendChild(image);
      overlay.appendChild(header);
      overlay.appendChild(stage);
      document.body.appendChild(overlay);
      return overlay;
    };

    this.openImageViewer = function (imageUrl, imageAlt) {
      if (typeof imageUrl !== "string" || !imageUrl.trim()) {
        return false;
      }

      this.closeImageViewer();
      var overlay = this.ensureImageViewer();
      var image = this.getPluginElement("fullscreen-image-viewer-image");
      var openLink = this.getPluginElement("fullscreen-image-viewer-open-link");
      if (!overlay || !image || !openLink) {
        return false;
      }

      this.imageViewerState.currentImageUrl = imageUrl.trim();
      image.src = this.imageViewerState.currentImageUrl;
      image.alt = typeof imageAlt === "string" && imageAlt.trim() ? imageAlt.trim() : "Chat image";
      openLink.href = this.imageViewerState.currentImageUrl;
      if (imageUrl.startsWith("blob:")) openLink.setAttribute("download", "chat-image");
      else openLink.removeAttribute("download");
      overlay.style.display = "flex";
      this.resetImageViewer();
      this.log("image_viewer_opened", { local: imageUrl.startsWith("blob:") });
      return true;
    };

    this.getIframeAllowedOrigins = function (iframe) {
      var allowed = this.config.iframeMessageAllowedOrigins || [];
      if (typeof allowed === "string") {
        allowed = [allowed];
      } else if (!Array.isArray(allowed)) {
        allowed = [];
      }

      if (iframe && iframe.src) {
        try {
          var iframeOrigin = new URL(iframe.src, window.location.href).origin;
          if (allowed.indexOf(iframeOrigin) === -1) {
            allowed.push(iframeOrigin);
          }
        } catch (error) {
          // Ignore invalid iframe src values.
        }
      }

      return allowed.filter(Boolean);
    };

    this.isAllowedIframeMessage = function (event, iframe) {
      if (!iframe || event.source !== iframe.contentWindow) {
        return false;
      }

      var allowedOrigins = this.getIframeAllowedOrigins(iframe);
      var isAllowed = allowedOrigins.indexOf(event.origin) !== -1;

      // Log origin check (important for debugging security issues)
      if (!isAllowed && this.debugEnabled) {
        this.log('iframe_message_rejected_origin', {
          level: 'warn',
          message: '[BotMe Plugin] Message rejected - origin not allowed',
          eventOrigin: event.origin,
          allowedOrigins
        });
      }

      return isAllowed;
    };

    this.forwardIframeMessageToHost = function (payload) {
      this.log('forwardIframeMessageToHost', { payloadType: payload.data?.type });

      if (typeof this.config.onChatCallback === "function") {
        this.config.onChatCallback(payload);
      } else {
        this.log('forwardIframeMessageToHost_invalid_callback', {
          level: 'warn',
          message: '[BotMe Plugin] onChatCallback is not a function'
        });
      }
    };

    this.handleManagedIframeMessage = function (event) {
      if (!event.data || typeof event.data !== "object") {
        return false;
      }

      if (event.data.type === "botme:popupDragStart") {
        const iframe = this.getPluginElement("corner-chat-element");
        const { clientX, clientY, screenX, screenY } = event.data;
        if (iframe && Number.isFinite(clientX) && Number.isFinite(clientY) &&
            Number.isFinite(screenX) && Number.isFinite(screenY) &&
            clientX >= 0 && clientX <= iframe.clientWidth &&
            clientY >= 0 && clientY <= iframe.clientHeight) {
          const rect = iframe.getBoundingClientRect();
          this.startPopupInteraction("move", rect.left + clientX, rect.top + clientY, { screenX, screenY });
        }
        return true;
      }

      if (event.data.type === "botme:popupDragMove") {
        const { screenX, screenY, buttons } = event.data;
        if (this.popupDragMove && Number.isFinite(screenX) && Number.isFinite(screenY) &&
            Number.isInteger(buttons)) {
          this.popupDragMove(screenX, screenY, buttons);
        }
        return true;
      }

      if (event.data.type === "botme:popupDragEnd") {
        this.endPopupInteraction();
        return true;
      }

      if (event.data.type === "botme:storeContext") {
        var nextContext = event.data.context;
        if (nextContext && typeof nextContext === "object") {
          this.pendingIframeContext = Object.assign({}, this.pendingIframeContext || {}, nextContext);
        }

        if (event.source && typeof event.source.postMessage === "function") {
          event.source.postMessage({ type: "botme:contextStored" }, event.origin || "*");
        }

        this.log('iframe_context_stored', {
          keys: Object.keys(this.pendingIframeContext || {})
        });

        return true;
      }

      if (event.data.type === "requestContext") {
        var context = Object.assign({}, this.pendingIframeContext || {});

        if (event.source && typeof event.source.postMessage === "function") {
          event.source.postMessage(
            {
              type: "contextResponse",
              context: context
            },
            event.origin || "*"
          );
        }

        this.log('iframe_context_sent', {
          keys: Object.keys(context)
        });

        return true;
      }

      if (event.data.type === "botme:probeImageViewer") {
        var probeId = event.data.requestId;
        if (typeof probeId !== "string" || probeId.length > 80 || !/^[a-zA-Z0-9-]+$/.test(probeId)) return true;
        this.imageViewerProbe = { requestId: probeId, source: event.source, origin: event.origin, expiresAtMs: Date.now() + 2000 };
        event.source.postMessage({ type: "botme:imageViewerReady", requestId: probeId }, event.origin);
        return true;
      }

      if (event.data.type === "botme:closeImageViewer") {
        if (event.data.requestId === this.imageViewerState.requestId) this.closeImageViewer();
        return true;
      }

      if (event.data.type === "botme:openImageViewer" && event.data.imageBlob !== undefined) {
        var probe = this.imageViewerProbe;
        var blob = event.data.imageBlob;
        var deadline = event.data.expiresAtMs;
        this.imageViewerProbe = null;
        if (!probe || probe.requestId !== event.data.requestId || probe.source !== event.source || probe.origin !== event.origin ||
            probe.expiresAtMs <= Date.now() || !(blob instanceof Blob) || blob.size <= 0 || blob.size > 10000000 ||
            !/^image\/(jpeg|png|webp|gif|avif|bmp|tiff|heic|heif)$/i.test(blob.type) ||
            !Number.isFinite(deadline) || deadline <= Date.now()) return true;
        var objectUrl = URL.createObjectURL(blob);
        if (!this.openImageViewer(objectUrl, event.data.imageAlt)) {
          URL.revokeObjectURL(objectUrl);
          return true;
        }
        this.imageViewerState.objectUrl = objectUrl;
        var extension = blob.type.slice(6).toLowerCase();
        this.getPluginElement("fullscreen-image-viewer-open-link").setAttribute("download", "chat-image." + (extension === "jpeg" ? "jpg" : extension));
        this.imageViewerState.requestId = event.data.requestId;
        this.imageViewerState.expiryTimer = setTimeout(() => this.closeImageViewer(), Math.min(deadline - Date.now(), 3600000));
        event.source.postMessage({ type: "botme:imageViewerOpened", requestId: event.data.requestId }, event.origin);
        return true;
      }

      // Keep URL-based requests working for older chat clients during independent rollouts.
      if (event.data.type === "botme:openImageViewer") {
        var didOpenImageViewer = this.openImageViewer(event.data.imageUrl, event.data.imageAlt);

        if (didOpenImageViewer && event.source && typeof event.source.postMessage === "function") {
          event.source.postMessage(
            {
              type: "botme:imageViewerOpened",
              imageUrl: event.data.imageUrl
            },
            event.origin || "*"
          );
        }

        return didOpenImageViewer;
      }

      return false;
    };

    this.setupIframeMessageListener = function () {
      if (this.isListeningToIframeMessages) {
        return;
      }

      this.isListeningToIframeMessages = true;
      this.log('setupIframeMessageListener', { status: 'attaching' });

      window.addEventListener('message', (event) => {
        const iframe = this.getPluginElement("corner-chat-element");
        const isFromIframe = !!(iframe && event.source === iframe.contentWindow);
        const payload = {
          origin: event.origin,
          data: event.data,
          isFromIframe: isFromIframe,
          hasIframe: !!iframe
        };

        this.log('message_received', {
          origin: event.origin,
          type: event.data?.type,
          isFromIframe
        });

        const isAllowedIframeMessage = this.isAllowedIframeMessage(event, iframe);

        if (isAllowedIframeMessage) {
          const handledInternally = this.handleManagedIframeMessage(event);

          if (handledInternally) {
            this.log('iframe_message_handled_internally', { type: event.data?.type });
          } else {
            // Forward allowed iframe messages to the hosting site so it can persist drafts to local storage.
            this.forwardIframeMessageToHost(payload);
            this.log('iframe_message_forwarded', { type: event.data?.type });
          }
        } else if (isFromIframe) {
          this.log('iframe_message_rejected', { origin: event.origin, type: event.data?.type });
        }

        if (this.config.logIframeMessages || this.debugEnabled) {
          this.log('iframe_message_debug', {
            origin: event.origin,
            type: event.data?.type,
            isFromIframe
          });
        }
      });

      this.log('setupIframeMessageListener', { status: 'completed' });
    };

    // Helper function to create legal links with consistent styling
    this.createLink = function (href, text) {
      var link = document.createElement("a");
      link.href = href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = text;
      link.style.display = "block";
      link.style.padding = "8px 16px";
      link.style.color = "#374151";
      link.style.textDecoration = "none";
      link.style.fontSize = "14px";
      link.style.transition = "background-color " + TRANSITION_FAST;
      link.onmouseover = function () {
        this.style.backgroundColor = "#f3f4f6";
      };
      link.onmouseout = function () {
        this.style.backgroundColor = "transparent";
      };
      return link;
    };

    this.createActionItem = function (id, text, onClick) {
      var item = document.createElement("button");
      item.id = this.getElementId(id);
      item.type = "button";
      item.textContent = text;
      item.style.display = "block";
      item.style.width = "100%";
      item.style.padding = "8px 16px";
      item.style.color = "#374151";
      item.style.textDecoration = "none";
      item.style.fontSize = "14px";
      item.style.textAlign = "left";
      item.style.border = "none";
      item.style.background = "transparent";
      item.style.cursor = "pointer";
      item.style.transition = "background-color " + TRANSITION_FAST;
      item.onmouseover = function () {
        this.style.backgroundColor = "#f3f4f6";
      };
      item.onmouseout = function () {
        this.style.backgroundColor = "transparent";
      };
      item.onclick = function (e) {
        e.preventDefault();
        onClick();
      };
      return item;
    };

    this.isMobilePortrait = function () {
      return window.innerHeight / window.innerWidth > 1;
    };

    // Helper function to create control buttons (close, minimize, info)
    this.createControlButton = function (id, iconSrc, size, options) {
      options = options || {};

      var button = document.createElement("img");
      button.id = this.getElementId(id);
      button.src = iconSrc;
      button.style.position = "fixed";

      // Vertical positioning
      if (this.calculatedDimensions.buttonTop !== undefined) {
        button.style.top = this.calculatedDimensions.buttonTop + 'px';
        button.style.bottom = "auto";
      } else if (this.calculatedDimensions.buttonBottom !== undefined) {
        button.style.bottom = this.calculatedDimensions.buttonBottom + 'px';
        button.style.top = "auto";
      }

      // Horizontal positioning (if offset provided)
      // offsetFromRight accounts for: padding + accumulated button widths + gaps
      if (options.offsetFromRightEdge !== undefined) {
        var padding = 30;
        var btnSize = parseFloat(size) || 32;

        if (this.calculatedDimensions.chatLeft !== null) {
          // Left-positioned chat: offset from right edge of chat (includes own button size)
          var leftOffset = padding + btnSize + (options.offsetFromRightEdge || 0);
          button.style.left = (this.calculatedDimensions.chatLeft + this.calculatedDimensions.chatWidth - leftOffset) + 'px';
          button.style.right = "auto";
        } else if (this.calculatedDimensions.chatRight !== null) {
          // Right-positioned chat: offset from right edge of viewport (excludes own button size)
          var rightOffset = padding + (options.offsetFromRightEdge || 0);
          button.style.right = (this.calculatedDimensions.chatRight + rightOffset) + 'px';
          button.style.left = "auto";
        }
      }

      // Common styles
      button.style.width = size;
      button.style.height = size;
      button.style.border = "none";
      button.style.cursor = "pointer";
      button.style.transform = SCALE_HIDDEN;
      button.style["transform-origin"] = "center";
      button.style["z-index"] = options.zIndex || this.config.closeButtonZIndex;

      // Optional styles
      if (options.borderRadius) button.style.borderRadius = options.borderRadius;
      if (options.background) button.style.background = options.background;
      if (options.boxShadow) button.style.boxShadow = options.boxShadow;
      if (options.display) button.style.display = options.display;
      if (options.alignItems) button.style.alignItems = options.alignItems;
      if (options.justifyContent) button.style.justifyContent = options.justifyContent;
      if (options.fontSize) button.style.fontSize = options.fontSize;
      if (options.fontWeight) button.style.fontWeight = options.fontWeight;

      // Transition
      var transition = "transform " + TRANSITION_TRANSFORM + ", background-color " + TRANSITION_FAST;
      if (options.includeOpacityTransition) {
        transition += ", opacity " + TRANSITION_FAST;
      }
      button.style.transition = transition;

      // Hover effects
      button.onmouseover = function () { this.style.opacity = 0.7; };
      button.onmouseout = function () { this.style.opacity = 1; };

      // Click handler
      if (options.onClick) {
        button.onclick = options.onClick;
      }

      return button;
    };

    this.generateAndAddFab = function () {
      if (window.disableBotMePlugin || !this.config.autoPlacement) return;
      if (!this.getConfiguredBotId()) return;

      // Use our default placed button
      let aiButtonElement = document.createElement("img");
      aiButtonElement.setAttribute("src", this.config.buttonIcon);

      aiButtonElement.id = this.getElementId("ai-agent-button");
      aiButtonElement.style.width = this.config.buttonSize;
      aiButtonElement.style.cursor = "pointer";
      aiButtonElement.alt = this.config.buttonTitle;
      aiButtonElement.title = this.config.buttonTitle;

      var aiButtonContainer = document.createElement("div");
      aiButtonContainer.style["z-index"] = this.config.buttonZIndex;
      aiButtonContainer.id = this.getElementId("aiButtonContainer");
      aiButtonContainer.style.position = "fixed";
      aiButtonContainer.style.width = this.config.buttonSize;
      aiButtonContainer.style.bottom = this.config.buttonPosition.bottom;
      aiButtonContainer.style.right = this.config.buttonPosition.right;
      if (this.config.buttonPosition.left) {
        aiButtonContainer.style.left = this.config.buttonPosition.left;
        aiButtonContainer.style.right = "auto";
      }
      if (this.config.buttonPosition.top) {
        aiButtonContainer.style.top = this.config.buttonPosition.top;
        aiButtonContainer.style.bottom = "auto";
      }

      aiButtonContainer.appendChild(aiButtonElement);
      document.body.appendChild(aiButtonContainer);

      aiButtonElement.addEventListener("click", (e) => {
        this.toggleChat(null, e);
      });

      return aiButtonElement;
    }

    this.attachToCustomTriggers = function () {
      // Find all img elements with the specific alt attribute
      const triggerImages = document.querySelectorAll('img[alt="Open a chat with BotMe AI agent"]');

      triggerImages.forEach((img) => {
        // Add pointer cursor
        img.style.cursor = "pointer";

        // Attach click event listener
        img.addEventListener("click", (e) => {
          this.toggleChat(null, e);
        });
      });

      if (triggerImages.length > 0) {
        this.log('custom_triggers_attached', {
          level: 'info',
          message: `BotMePlugin: Attached ${triggerImages.length} custom trigger(s)`,
          triggerCount: triggerImages.length
        });
      }
    }

    this.endPopupInteraction = function () {
      if (this.popupInteractionCleanup) {
        this.popupInteractionCleanup();
        this.popupInteractionCleanup = null;
      }
    };

    this.resetPopupDragTilt = function () {
      const tilt = this.popupDragTilt;
      if (!tilt) return;
      cancelAnimationFrame(tilt.frameId);
      tilt.iframe.style.rotate = "";
      tilt.iframe.style.transformOrigin = tilt.transformOrigin;
      tilt.iframe.style.willChange = tilt.willChange;
      ["corner-chat-close-button", "corner-chat-minimize-button", "corner-chat-info-button",
        "corner-chat-resize-handles"].forEach((id) => {
        const element = this.getPluginElement(id);
        if (!element) return;
        element.style.rotate = "";
        element.style.translate = "";
      });
      this.popupDragTilt = null;
      this.updateButtonPositions();
    };

    this.startPopupDragTilt = function (iframe, initialLeft) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return null;
      const tilt = {
        iframe, angle: 0, target: 0, frameId: null, dragging: true,
        lastLeft: initialLeft, lastMoveAt: performance.now(), lastFrameAt: performance.now(),
        transformOrigin: iframe.style.transformOrigin, willChange: iframe.style.willChange
      };
      this.popupDragTilt = tilt;
      iframe.style.transformOrigin = "center center";
      iframe.style.willChange = "rotate";
      const animate = (now) => {
        tilt.frameId = null;
        // Ease back to upright when the pointer rests, as well as on release.
        if (now - tilt.lastMoveAt > 90) tilt.target = 0;
        const elapsed = Math.min(64, Math.max(0, now - tilt.lastFrameAt));
        tilt.lastFrameAt = now;
        tilt.angle += (tilt.target - tilt.angle) * (1 - Math.exp(-elapsed / 75));
        if (Math.abs(tilt.target - tilt.angle) < 0.01) tilt.angle = tilt.target;
        iframe.style.rotate = tilt.angle + "deg";
        this.updateButtonPositions();
        if (!tilt.dragging && tilt.angle === 0) {
          this.resetPopupDragTilt();
        } else if (tilt.angle !== 0 || tilt.target !== 0) {
          tilt.frameId = requestAnimationFrame(animate);
        }
      };
      const schedule = () => {
        if (tilt.frameId !== null) return;
        tilt.lastFrameAt = performance.now();
        tilt.frameId = requestAnimationFrame(animate);
      };
      tilt.move = (left) => {
        const now = performance.now();
        const movement = left - tilt.lastLeft;
        if (movement === 0) return;
        // Use actual horizontal movement so reversing direction reverses the tilt.
        tilt.target = Math.max(-3, Math.min(3, movement / Math.max(8, now - tilt.lastMoveAt) * 5));
        tilt.lastLeft = left;
        tilt.lastMoveAt = now;
        schedule();
      };
      tilt.end = () => {
        tilt.dragging = false;
        tilt.target = 0;
        schedule();
      };
      return tilt;
    };

    this.isModalDragAndResizeEnabled = function () {
      return (this.lastKnownDisplayMode || this.config.displayMode) === "modal" &&
        this.config.modalDragAndResize !== false;
    };

    this.startPopupInteraction = function (edge, clientX, clientY, screenOrigin) {
      const iframe = this.getPluginElement("corner-chat-element");
      if (!iframe || !this.isOpened || this.isMinimized || !this.isModalDragAndResizeEnabled()) return;

      this.endPopupInteraction();
      this.resetPopupDragTilt();
      const rect = iframe.getBoundingClientRect();
      // Do not start an interaction during the popup's scale animation.
      if (Math.abs(rect.width - iframe.offsetWidth) > 1 || rect.width < 1) return;

      // Normalize the anchor once; moving thereafter changes only left and bottom.
      iframe.style.right = "auto";
      iframe.style.top = "auto";
      iframe.style.left = rect.left + "px";
      iframe.style.bottom = (window.innerHeight - rect.bottom) + "px";
      const tilt = edge === "move" ? this.startPopupDragTilt(iframe, rect.left) : null;
      if (edge === "move") this.closeInfoPopup();

      // Capture mouse events above the iframe, even when the mouse leaves its bounds.
      const shield = document.createElement("div");
      shield.id = this.getElementId("corner-chat-interaction-shield");
      Object.assign(shield.style, {
        position: "fixed", inset: "0", background: "transparent",
        zIndex: Math.max(this.config.chatZIndex, this.config.closeButtonZIndex) + 1,
        cursor: edge === "move" ? "grabbing" : edge + "-resize",
        userSelect: "none"
      });
      document.body.appendChild(shield);

      const clamp = (value, min, max) => Math.max(min, Math.min(value, max));
      const onMove = (event) => {
        if ((event.buttons & 1) === 0) {
          this.endPopupInteraction();
          return;
        }
        event.preventDefault();
        const dx = event.clientX - clientX;
        const dy = event.clientY - clientY;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        if (edge === "move") {
          const left = clamp(rect.left + dx, 0, Math.max(0, viewportWidth - rect.width));
          iframe.style.left = left + "px";
          iframe.style.bottom = clamp(viewportHeight - rect.bottom - dy, 0,
            Math.max(0, viewportHeight - rect.height)) + "px";
          if (tilt) tilt.move(left);
        } else {
          const minWidth = Math.min(280, viewportWidth);
          const minHeight = Math.min(240, viewportHeight);
          let left = clamp(rect.left, 0, Math.max(0, viewportWidth - minWidth));
          let right = clamp(rect.right, left + minWidth, viewportWidth);
          let top = clamp(rect.top, 0, Math.max(0, viewportHeight - minHeight));
          let bottom = clamp(rect.bottom, top + minHeight, viewportHeight);
          if (edge.includes("w")) left = clamp(rect.left + dx, 0, right - minWidth);
          if (edge.includes("e")) right = clamp(rect.right + dx, left + minWidth, viewportWidth);
          if (edge.includes("n")) top = clamp(rect.top + dy, 0, bottom - minHeight);
          if (edge.includes("s")) bottom = clamp(rect.bottom + dy, top + minHeight, viewportHeight);
          iframe.style.left = left + "px";
          iframe.style.bottom = (viewportHeight - bottom) + "px";
          iframe.style.width = (right - left) + "px";
          iframe.style.height = (bottom - top) + "px";
          // User resizing may grow beyond the initial configured maxHeight.
          iframe.style.maxHeight = "none";
        }
        this.updateButtonPositions();
      };
      const onEnd = () => this.endPopupInteraction();
      // A press inside a cross-origin iframe can retain native mouse capture.
      // Screen coordinates stay stable while moving the iframe under the mouse.
      if (edge === "move" && screenOrigin) {
        this.popupDragMove = (screenX, screenY, buttons) => onMove({
          clientX: clientX + screenX - screenOrigin.screenX,
          clientY: clientY + screenY - screenOrigin.screenY,
          buttons,
          preventDefault() {}
        });
      }
      window.addEventListener("mousemove", onMove, true);
      window.addEventListener("mouseup", onEnd, true);
      window.addEventListener("blur", onEnd);
      this.popupInteractionCleanup = () => {
        if (tilt) tilt.end();
        this.popupDragMove = null;
        window.removeEventListener("mousemove", onMove, true);
        window.removeEventListener("mouseup", onEnd, true);
        window.removeEventListener("blur", onEnd);
        shield.remove();
      };
    };

    this.createPopupResizeHandles = function () {
      if (!this.isModalDragAndResizeEnabled()) return;
      const handles = document.createElement("div");
      handles.id = this.getElementId("corner-chat-resize-handles");
      handles.setAttribute("aria-hidden", "true");
      Object.assign(handles.style, {
        position: "fixed", pointerEvents: "none", display: "none",
        zIndex: this.config.chatZIndex
      });
      const positions = {
        n: { top: "-4px", left: "8px", right: "8px", height: "8px" },
        s: { bottom: "-4px", left: "8px", right: "8px", height: "8px" },
        w: { left: "-4px", top: "8px", bottom: "8px", width: "8px" },
        e: { right: "-4px", top: "8px", bottom: "8px", width: "8px" },
        nw: { left: "-4px", top: "-4px", width: "12px", height: "12px" },
        ne: { right: "-4px", top: "-4px", width: "12px", height: "12px" },
        sw: { left: "-4px", bottom: "-4px", width: "12px", height: "12px" },
        se: { right: "-4px", bottom: "-4px", width: "12px", height: "12px" }
      };
      Object.keys(positions).forEach((edge) => {
        const handle = document.createElement("div");
        handle.dataset.botmeResizeEdge = edge;
        Object.assign(handle.style, positions[edge], {
          position: "absolute", pointerEvents: "auto", cursor: edge + "-resize"
        });
        handle.addEventListener("mousedown", (event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          this.startPopupInteraction(edge, event.clientX, event.clientY);
        });
        handles.appendChild(handle);
      });
      document.body.appendChild(handles);
    };

    this.updatePopupResizeHandles = function (rect) {
      const handles = this.getPluginElement("corner-chat-resize-handles");
      if (!handles) return;
      handles.style.display = this.isOpened && !this.isMinimized && rect.width > 1 ? "block" : "none";
      handles.style.left = rect.left + "px";
      handles.style.top = rect.top + "px";
      handles.style.width = rect.width + "px";
      handles.style.height = rect.height + "px";
    };

    // Position the buttons relative to the iframe's actual position.
    this.updateButtonPositions = function () {
      const iframe = this.getPluginElement("corner-chat-element");
      const closeButton = this.getPluginElement("corner-chat-close-button");
      const minimizeButton = this.getPluginElement("corner-chat-minimize-button");
      const infoButton = this.getPluginElement("corner-chat-info-button");
      const infoPopup = this.getPluginElement("corner-chat-info-popup");

      if (!iframe || !closeButton) return;

      // Rotation is visual only; use the layout bounds to keep drag and resize stable.
      const rect = this.popupDragTilt ? {
        left: iframe.offsetLeft, top: iframe.offsetTop,
        right: iframe.offsetLeft + iframe.offsetWidth,
        bottom: iframe.offsetTop + iframe.offsetHeight,
        width: iframe.offsetWidth, height: iframe.offsetHeight
      } : iframe.getBoundingClientRect();
      this.updatePopupResizeHandles(rect);
      const padding = 8;     // distance from iframe edges
      const gap = 6;         // gap between buttons

      const closeSize = parseFloat(this.config.closeButtonSize || '32');
      const minimizeSize = parseFloat(this.config.minimizeButtonSize || '32');
      const infoSize = 32;   // hardcoded in your code

      // Top position for all buttons (relative to viewport)
      const top = rect.top + padding;

      // Right side of iframe in viewport coordinates:
      // distance from right edge of viewport to iframe's right edge
      let right = (window.innerWidth - rect.right) + padding;

      // Close button (right-most)
      closeButton.style.position = 'fixed';
      closeButton.style.top = top + 'px';
      closeButton.style.right = right + 'px';
      closeButton.style.left = 'auto';
      closeButton.style.bottom = 'auto';

      // Minimize button (left of close)
      if (minimizeButton) {
        right += closeSize + gap;
        minimizeButton.style.position = 'fixed';
        minimizeButton.style.top = top + 'px';
        minimizeButton.style.right = right + 'px';
        minimizeButton.style.left = 'auto';
        minimizeButton.style.bottom = 'auto';
      }

      // Info button (left of minimize)
      if (infoButton) {
        right += minimizeSize + gap;
        infoButton.style.position = 'fixed';
        infoButton.style.top = top + 'px';
        infoButton.style.right = right + 'px';
        infoButton.style.left = 'auto';
        infoButton.style.bottom = 'auto';
      }

      // Info popup – open under the buttons, right-aligned with iframe
      if (infoPopup) {
        const popupTop = top + closeSize + 4; // 4px below the buttons
        const popupRight = (window.innerWidth - rect.right) + padding;

        infoPopup.style.position = 'fixed';
        infoPopup.style.top = popupTop + 'px';
        infoPopup.style.right = popupRight + 'px';
        infoPopup.style.left = 'auto';
        infoPopup.style.bottom = 'auto';
      }

      if (this.popupDragTilt) {
        const angle = this.popupDragTilt.angle;
        const radians = angle * Math.PI / 180;
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        // Rotate the separate floating controls around the same center as the chat.
        [closeButton, minimizeButton, infoButton].forEach((button) => {
          if (!button) return;
          const dx = button.offsetLeft + button.offsetWidth / 2 - centerX;
          const dy = button.offsetTop + button.offsetHeight / 2 - centerY;
          button.style.translate = (dx * Math.cos(radians) - dy * Math.sin(radians) - dx) + "px " +
            (dx * Math.sin(radians) + dy * Math.cos(radians) - dy) + "px";
          button.style.rotate = angle + "deg";
        });
        const handles = this.getPluginElement("corner-chat-resize-handles");
        if (handles) handles.style.rotate = angle + "deg";
      }

      this.log('updateButtonPositions', {
        iframeRect: rect,
        closeButton: {
          top: closeButton.style.top,
          right: closeButton.style.right
        }
      });
    };

    this.calculateDynamicPosition = function (mouseX, mouseY, chatWidth, chatHeight, maxHeight) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 20; // Minimum padding from viewport edges

      // Parse dimensions to pixels (handles %, px, vh, vw)
      const widthValue = this.parseDimension(chatWidth, viewportWidth);
      const heightValue = this.parseDimension(chatHeight, viewportHeight);
      // Account for maxHeight constraint - actual height is the minimum of the two
      const maxHeightValue = maxHeight ? this.parseDimension(maxHeight, viewportHeight) : heightValue;
      const actualHeight = Math.min(heightValue, maxHeightValue);
      const totalHeight = actualHeight;

      let position = {
        vertical: 'bottom', // 'top' or 'bottom'
        horizontal: 'left',  // 'left' or 'right'
        top: null,
        bottom: null,
        left: null,
        right: null
      };

      // Calculate vertical position - ensure popup stays within viewport bounds
      // Preferred: above the mouse click (bottom positioning, popup grows upward)
      const spaceAbove = mouseY;
      const spaceBelow = viewportHeight - mouseY;

      if (spaceAbove >= totalHeight + padding) {
        // Enough space above - position so the popup bottom is at mouse Y
        position.vertical = 'bottom';
        position.bottom = (viewportHeight - mouseY) + 'px';
      } else if (spaceBelow >= totalHeight + padding) {
        // Not enough space above, use below
        position.vertical = 'top';
        position.top = mouseY + 'px';
      } else {
        // Not enough space in either direction - position at edge with most space
        // CSS maxHeight will constrain the popup if it's too tall
        if (spaceAbove > spaceBelow) {
          position.vertical = 'bottom';
          position.bottom = padding + 'px';
        } else {
          position.vertical = 'top';
          position.top = padding + 'px';
        }
      }

      // Calculate horizontal position - ensure popup stays within viewport bounds
      // Default: left of mouse click (right positioning, popup grows leftward)
      const spaceOnLeft = mouseX;
      const spaceOnRight = viewportWidth - mouseX;

      if (spaceOnLeft >= widthValue + padding) {
        // Enough space on left - position so popup right edge is at mouse X
        position.horizontal = 'left';
        position.right = (viewportWidth - mouseX) + 'px';
      } else if (spaceOnRight >= widthValue + padding) {
        // Not enough space on left, use right
        position.horizontal = 'right';
        position.left = mouseX + 'px';
      } else {
        // Not enough space in either direction - position at edge with most space
        // Popup will be constrained by viewport width
        if (spaceOnLeft > spaceOnRight) {
          position.horizontal = 'left';
          position.right = padding + 'px';
        } else {
          position.horizontal = 'right';
          position.left = padding + 'px';
        }
      }

      return position;
    };

    // Helper to convert CSS values to pixels
    this.parseDimension = function (value, referenceSize) {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        if (value.endsWith('px')) return parseFloat(value);
        if (value.endsWith('%')) return (parseFloat(value) / 100) * referenceSize;
        if (value.endsWith('vh')) return (parseFloat(value) / 100) * window.innerHeight;
        if (value.endsWith('vw')) return (parseFloat(value) / 100) * window.innerWidth;
        return parseFloat(value);
      }
      return 0;
    };

    this.openChat = function (botId, mouseX, mouseY) {
      if (this.isOpened) {
        return;
      }

      // botId is the canonical public identifier.
      // Keep config.apiKey as a deprecated alias for older embeds.
      const key = this.resolveBotId(botId) || this.getConfiguredBotId();
      if (!key) {
        this.log('openChat_missing_bot_id', {
          level: 'error',
          message: 'BotMePlugin: No bot ID provided'
        });
        return;
      }

      this.setupIframeMessageListener();

      const isMobile = this.isMobilePortrait();
      const dimensions = isMobile ? this.config.chatDimensions.mobile : this.config.chatDimensions.desktop;

      // Calculate actual pixel dimensions once
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const buttonSpacingPx = 30;

      // Calculate actual constrained chat height (accounting for maxHeight)
      const chatHeightPx = this.parseDimension(dimensions.height, viewportHeight);
      const maxHeightPx = dimensions.maxHeight ? this.parseDimension(dimensions.maxHeight, viewportHeight) : chatHeightPx;
      const actualChatHeight = Math.min(chatHeightPx, maxHeightPx);

      this.calculatedDimensions = {
        chatWidth: this.parseDimension(dimensions.width, viewportWidth),
        chatHeight: actualChatHeight, // Use actual constrained height
        chatBottom: this.parseDimension(this.config.chatPosition.bottom || '20px', viewportHeight),
        chatLeft: this.config.chatPosition.left ? this.parseDimension(this.config.chatPosition.left, viewportWidth) : null,
        chatRight: this.config.chatPosition.right ? this.parseDimension(this.config.chatPosition.right, viewportWidth) : null,
        chatTop: this.config.chatPosition.top ? this.parseDimension(this.config.chatPosition.top, viewportHeight) : null,
        buttonSpacing: buttonSpacingPx
      };

      // Calculate button positions based on actual chat dimensions
      if (this.calculatedDimensions.chatBottom !== null) {
        this.calculatedDimensions.buttonBottom = this.calculatedDimensions.chatBottom +
          this.calculatedDimensions.chatHeight +
          this.calculatedDimensions.buttonSpacing;
      } else if (this.calculatedDimensions.chatTop !== null) {
        this.calculatedDimensions.buttonTop = this.calculatedDimensions.chatTop +
          this.calculatedDimensions.chatHeight +
          this.calculatedDimensions.buttonSpacing;
      }

      // Calculate dynamic position if enabled and mouse coordinates are provided
      let dynamicPos = null;
      if (this.config.dynamicAnchor && mouseX !== null && mouseY !== null) {
        dynamicPos = this.calculateDynamicPosition(mouseX, mouseY, dimensions.width, dimensions.height, dimensions.maxHeight);
        this.log('dynamic_anchor_calculated', {
          config: { dynamicAnchor: this.config.dynamicAnchor },
          mouse: { x: mouseX, y: mouseY },
          viewport: { width: window.innerWidth, height: window.innerHeight },
          chatDimensions: dimensions,
          calculatedPosition: dynamicPos
        });
      } else {
        this.log('dynamic_anchor_skipped', {
          config: { dynamicAnchor: this.config.dynamicAnchor },
          mouse: { x: mouseX, y: mouseY }
        });
      }

      // Build chat URL with query params
      let chatUrl = `${BOTME_BASE_URL}/fetch/embedded-chat.html?botId=${encodeURIComponent(key)}`;
      if (this.config.chatBackgroundColor) {
        // Remove # prefix if present
        const bgColor = this.config.chatBackgroundColor.replace(/^#/, '');
        chatUrl += `&backgroundColor=${encodeURIComponent(bgColor)}`;
      }
      if (this.config.chatHeaderColor) {
        // Remove # prefix if present
        const headerColor = this.config.chatHeaderColor.replace(/^#/, '');
        chatUrl += `&headerColor=${encodeURIComponent(headerColor)}`;
      }
      if (this.config.chatTitle) {
        chatUrl += `&botTitle=${encodeURIComponent(this.config.chatTitle)}`;
      }
      const themeMode = this.getThemeMode();
      if (themeMode) {
        chatUrl += `&themeMode=${encodeURIComponent(themeMode)}`;
      }
      const interfaceLanguageCode = this.resolveInterfaceLanguageCode();
      if (interfaceLanguageCode) {
        chatUrl += `&interfaceLanguageCode=${encodeURIComponent(interfaceLanguageCode)}`;
      }

      // Check which display mode is enabled
      const displayMode = this.lastKnownDisplayMode || this.config.displayMode;

      if (this.isModalDragAndResizeEnabled()) {
        chatUrl += "&popupMovable=1";
      }

      // Create iframe
      this.pendingIframeContext = null;
      var iframe = document.createElement("iframe");
      iframe.id = this.getElementId("corner-chat-element");
      // Timers can run before the browser finishes the opening/restore transition.
      // Re-align controls to the final bounds so a stale resize handle cannot cover the header.
      iframe.addEventListener("transitionend", (event) => {
        if (event.target === iframe && event.propertyName === "transform" &&
            this.getPluginElement("corner-chat-element") === iframe) {
          this.updateButtonPositions();
        }
      });
      // Keep copying available after the loader navigates to the chat app.
      iframe.setAttribute("allow", "clipboard-write " +
        BOTME_MANAGED_CHAT_ORIGINS.concat("https://botme-chat.web.app").join(" "));
      iframe.src = chatUrl;
      iframe.style.border = "none";
      iframe.style.position = "fixed";
      var isDrawerMode = displayMode === "drawer-left" || displayMode === "drawer-right";
      var isDrawerLeft = displayMode === "drawer-left";
      var isFullscreenPopupMode = displayMode === "fullscreen-popup";

      if (isDrawerMode) {
        // Drawer mode: full height, positioned at left or right edge
        iframe.style.top = "0";
        iframe.style.bottom = "0";
        // Use dvh on mobile when supported; fall back to vh for older browsers
        const supportsDvh = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh');
        if (!supportsDvh) {
          // Very rare situation, here's why:
          /*
            Mobile Browsers (where it matters most) WITHOUT dvh support:
            - iOS Safari < 15.4 (released March 2022)
              - iOS 15.0 - 15.3
              - iOS 14 and older
            - Chrome Android < 108 (released November 2022)
            - Samsung Internet < 20 (released early 2023)
            - Firefox Android < 101 (released May 2022)
          */
        }
        const viewportUnit = isMobile && supportsDvh ? '100dvh' : '100vh';
        iframe.style.height = viewportUnit;
        iframe.style.width = dimensions.width;

        if (isDrawerLeft) {
          iframe.style.left = "0";
          iframe.style.right = "auto";
          iframe.style["border-radius"] = `0 ${this.config.chatBorderRadius} ${this.config.chatBorderRadius} 0`;
          iframe.style.transform = "translateX(-100%)"; // Start off-screen left
        } else {
          iframe.style.right = "0";
          iframe.style.left = "auto";
          iframe.style["border-radius"] = `${this.config.chatBorderRadius} 0 0 ${this.config.chatBorderRadius}`;
          iframe.style.transform = "translateX(100%)"; // Start off-screen right
        }
      } else if (isFullscreenPopupMode) {
        const supportsDvh = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh');
        const viewportUnit = isMobile && supportsDvh ? '100dvh' : '100vh';
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.right = "auto";
        iframe.style.bottom = "auto";
        iframe.style.width = "100vw";
        iframe.style.height = viewportUnit;
        iframe.style["max-height"] = viewportUnit;
        iframe.style["border-radius"] = "0";
        iframe.style.transform = SCALE_HIDDEN;
        iframe.style["transform-origin"] = "center center";
      } else {
        // Modal mode: original positioning logic
        // Apply positioning based on dynamic anchor or config
        if (dynamicPos) {
          // Dynamic positioning
          if (dynamicPos.bottom !== null) {
            iframe.style.bottom = dynamicPos.bottom;
            iframe.style.top = "auto";
          } else if (dynamicPos.top !== null) {
            iframe.style.top = dynamicPos.top;
            iframe.style.bottom = "auto";
          }

          if (dynamicPos.right !== null) {
            iframe.style.right = dynamicPos.right;
            iframe.style.left = "auto";
          } else if (dynamicPos.left !== null) {
            iframe.style.left = dynamicPos.left;
            iframe.style.right = "auto";
          }
        } else {
          // Static positioning from config
          iframe.style.bottom = this.config.chatPosition.bottom;
          iframe.style.right = this.config.chatPosition.right;
          if (this.config.chatPosition.left) {
            iframe.style.left = this.config.chatPosition.left;
            iframe.style.right = "auto";
          }
          if (this.config.chatPosition.top) {
            iframe.style.top = this.config.chatPosition.top;
            iframe.style.bottom = "auto";
          }
        }

        iframe.style.width = dimensions.width;
        iframe.style.height = dimensions.height;
        iframe.style["max-height"] = dimensions.maxHeight;
        iframe.style["border-radius"] = this.config.chatBorderRadius;
        iframe.style.transform = SCALE_HIDDEN;

        // Set transform origin based on position
        // Note: transform-origin should be opposite of growth direction
        // If popup grows leftward (horizontal='left'), origin should be right
        var transformOrigin = "bottom right";
        if (dynamicPos) {
          var horizontalOrigin = dynamicPos.horizontal === 'left' ? 'right' : 'left';
          transformOrigin = `${dynamicPos.vertical} ${horizontalOrigin}`;
        }
        iframe.style["transform-origin"] = transformOrigin;
      }

      iframe.style.background = "#fff";
      iframe.style["box-shadow"] = this.config.chatBoxShadow;
      iframe.style["z-index"] = this.config.chatZIndex;
      iframe.style.transition = "transform " + TRANSITION_TRANSFORM;

      // Create overlay background
      var overlay = document.createElement("div");
      overlay.id = this.getElementId("corner-chat-overlay");
      overlay.style.position = "fixed";
      overlay.style.top = "0";
      overlay.style.left = "0";
      overlay.style.width = "100%";
      overlay.style.height = "100%";
      overlay.style["background-color"] = this.config.overlayBackground;
      overlay.style["z-index"] = this.config.overlayZIndex;
      overlay.style.opacity = OPACITY_HIDDEN;
      overlay.style.transition = "opacity " + TRANSITION_TRANSFORM;
      overlay.onclick = () => {
        this.minimizeChat();
      };

      // Create transparent overlay for info popup (to catch all clicks)
      var infoOverlay = document.createElement("div");
      infoOverlay.id = this.getElementId("corner-chat-info-overlay");
      infoOverlay.style.position = "fixed";
      infoOverlay.style.top = "0";
      infoOverlay.style.left = "0";
      infoOverlay.style.width = "100%";
      infoOverlay.style.height = "100%";
      infoOverlay.style.backgroundColor = "transparent";
      infoOverlay.style.zIndex = String(this.config.chatZIndex + 10);
      infoOverlay.style.display = "none";
      infoOverlay.style.cursor = "default";
      infoOverlay.onclick = () => {
        this.closeInfoPopup();
      };

      // Create close button
      // Track if dynamic positioning was used for transform calculations
      var usesDynamicTransform = dynamicPos || (!dynamicPos && this.config.chatPosition.bottom);
      this.usedDynamicPositioning = usesDynamicTransform;

      var closeButton = this.createControlButton(
        "corner-chat-close-button",
        this.config.closeButtonIcon,
        this.config.closeButtonSize,
        {
          offsetFromRightEdge: 0,  // First button, no accumulated offset
          background: "#fff0",
          onClick: () => { this.closeChat(); }
        }
      );

      // Create minimize button
      var minimizeButton = this.createControlButton(
        "corner-chat-minimize-button",
        this.config.minimizeButtonIcon,
        this.config.minimizeButtonSize,
        {
          offsetFromRightEdge: 32 + 10,  // Close button (32px) + gap (10px)
          borderRadius: "50%",
          onClick: () => { this.minimizeChat(); }
        }
      );

      // Create info button (legal links)
      var infoButton = this.createControlButton(
        "corner-chat-info-button",
        this.config.menuButtonIcon,
        "32px",
        {
          offsetFromRightEdge: 32 + 10 + 32 + 10,  // Close + gap + Minimize + gap
          borderRadius: "50%",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          fontWeight: "bold",
          zIndex: String(this.config.chatZIndex + 11),
          includeOpacityTransition: true,
          onClick: () => { this.toggleInfoPopup(); }
        }
      );

      // Create info popup
      var infoPopup = document.createElement("div");
      infoPopup.id = this.getElementId("corner-chat-info-popup");
      infoPopup.style.position = "fixed";
      infoPopup.style.display = "flex";
      infoPopup.style.flexDirection = "column";

      // Position info popup based on dynamic anchor or config
      if (dynamicPos) {
        if (dynamicPos.vertical === 'bottom') {
          infoPopup.style.bottom = `calc(${dynamicPos.bottom} - 30px - 32px - 8px)`;
          infoPopup.style.top = "auto";
        } else {
          // Position popup aligned with button top
          // Button CSS top: dynamicPos.top + 30px + 78px (after fix)
          // Button visual top after transform: (dynamicPos.top + 30px + 78px) - 78px = dynamicPos.top + 30px
          // Popup top should align with button top
          infoPopup.style.top = `calc(${dynamicPos.top} + 30px)`;
          infoPopup.style.bottom = "auto";
        }

        if (dynamicPos.horizontal === 'left') {
          infoPopup.style.right = `calc(${dynamicPos.right} + 30px + ${this.config.closeButtonSize} + 10px + ${this.config.minimizeButtonSize} + 10px)`;
          infoPopup.style.left = "auto";
        } else {
          // Position popup aligned to the right edge of the info button
          // Button left: dynamicPos.left + width - 30px - closeBtn - 10px - minimizeBtn - 10px - 32px
          // Button right edge: button left + 32px
          // Popup should align its right edge with button's right edge
          infoPopup.style.right = `calc(100vw - ${dynamicPos.left} - ${dimensions.width} + 30px + ${this.config.closeButtonSize} + 10px + ${this.config.minimizeButtonSize} + 10px)`;
          infoPopup.style.left = "auto";
        }
      } else {
        // Static mode: position popup relative to button (like dynamic anchor mode)
        // Button has transform: translateY(-78px), so visual position is offset
        if (this.calculatedDimensions.buttonTop !== undefined) {
          // Top-positioned button: popup appears below
          // Visual button top = buttonTop + 78 (transform moves down visually from top)
          // Popup top = visual button top + 32 (button height) + 8 (spacing)
          infoPopup.style.top = (this.calculatedDimensions.buttonTop + 78 + 32 + 8) + 'px';
          infoPopup.style.bottom = "auto";
        } else if (this.calculatedDimensions.buttonBottom !== undefined) {
          // Bottom-positioned button: popup appears above
          // Visual button bottom = buttonBottom - 78 (transform moves up)
          // Visual button top = buttonBottom - 78 + 32 (button height)
          // Popup top should align with button top
          infoPopup.style.bottom = (this.calculatedDimensions.buttonBottom - 78 + 32) + 'px';
          infoPopup.style.top = "auto";
        }

        // Horizontal positioning - align popup right edge with info button right edge
        // Constrain popup to stay within chat iframe boundaries
        if (this.calculatedDimensions.chatLeft !== null) {
          // Chat is left-positioned
          // Info button left = chatLeft + chatWidth - 30 - 32 - 10 - 32 - 10 - 32
          // Info button right edge = above + 32 = chatLeft + chatWidth - 146 + 32 = chatLeft + chatWidth - 114
          // Popup right edge aligns with button right edge
          const buttonRightEdge = this.calculatedDimensions.chatLeft + this.calculatedDimensions.chatWidth - 114;
          let popupRight = viewportWidth - buttonRightEdge;

          // Ensure popup doesn't extend beyond chat left edge
          // Chat left edge is at: viewportWidth - chatLeft - chatWidth
          const chatRightEdge = viewportWidth - this.calculatedDimensions.chatLeft - this.calculatedDimensions.chatWidth;
          // Popup has minWidth 160px, so: popupRight - 160 >= chatRightEdge
          // Therefore: popupRight >= chatRightEdge + 160
          popupRight = Math.max(popupRight, chatRightEdge + 160);

          infoPopup.style.right = popupRight + 'px';
          infoPopup.style.left = "auto";
        } else if (this.calculatedDimensions.chatRight !== null) {
          // Chat is right-positioned
          // Info button right = chatRight + 30 + 32 + 10 + 32 + 10
          // Popup right edge aligns with button right edge (add 32 for button width)
          let popupRight = this.calculatedDimensions.chatRight + 30 + 32 + 10 + 32 + 10 + 32;

          // Ensure popup doesn't extend beyond chat left edge
          // Chat extends from right edge to: right + chatWidth
          // Popup should not go beyond: right + chatWidth
          // Popup left edge = popupRight + minWidth, so: popupRight + 160 <= chatRight + chatWidth
          // Therefore: popupRight <= chatRight + chatWidth - 160
          const maxPopupRight = this.calculatedDimensions.chatRight + this.calculatedDimensions.chatWidth - 160;
          popupRight = Math.min(popupRight, maxPopupRight);

          infoPopup.style.right = popupRight + 'px';
          infoPopup.style.left = "auto";
        }

        // Vertical constraint: ensure popup doesn't extend beyond chat boundaries
        if (this.calculatedDimensions.buttonBottom !== undefined) {
          const chatBottomEdge = this.calculatedDimensions.chatBottom;

          // Position popup inside chat with proper offset
          // Adjusted to position popup 100px lower (inside chat boundaries)
          const currentPopupBottom = this.calculatedDimensions.buttonBottom - 78 + 32 - 100;
          const constrainedPopupBottom = Math.max(currentPopupBottom, chatBottomEdge);

          infoPopup.style.bottom = constrainedPopupBottom + 'px';
        }
      }
      infoPopup.style.backgroundColor = "#fff";
      infoPopup.style.borderRadius = "8px";
      infoPopup.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
      infoPopup.style.padding = "4px 0";
      infoPopup.style.paddingTop = "28px";
      infoPopup.style.minWidth = "160px";
      infoPopup.style["z-index"] = String(this.config.chatZIndex + 11);
      infoPopup.style.opacity = OPACITY_HIDDEN;
      infoPopup.style.transform = SCALE_HIDDEN;
      // Set transform origin based on positioning - animate from near button
      // For bottom-positioned buttons, popup appears above, so animate from bottom-right
      var popupTransformOrigin = "bottom right";
      if (dynamicPos) {
        var popupVertical = dynamicPos.vertical === 'bottom' ? 'bottom' : 'top';
        var popupHorizontal = dynamicPos.horizontal === 'left' ? 'left' : 'right';
        popupTransformOrigin = `${popupVertical} ${popupHorizontal}`;
      } else if (this.calculatedDimensions.buttonTop !== undefined) {
        popupTransformOrigin = "top right";
      }
      infoPopup.style.transformOrigin = popupTransformOrigin;
      infoPopup.style.transition = "opacity " + TRANSITION_OPACITY + ", transform " + TRANSITION_OPACITY;
      infoPopup.style.pointerEvents = "none";

      // Close button for popup
      var popupCloseButton = document.createElement("img");
      popupCloseButton.src = this.config.closeButtonIcon;
      popupCloseButton.style.position = "absolute";
      popupCloseButton.style.top = "4px";
      popupCloseButton.style.right = "4px";
      popupCloseButton.style.width = "20px";
      popupCloseButton.style.height = "20px";
      popupCloseButton.style.cursor = "pointer";
      popupCloseButton.style.opacity = "0.6";
      popupCloseButton.style.transition = "opacity " + TRANSITION_FAST;
      popupCloseButton.onmouseover = function () {
        this.style.opacity = "1";
      };
      popupCloseButton.onmouseout = function () {
        this.style.opacity = "0.6";
      };
      popupCloseButton.onclick = () => {
        this.closeInfoPopup();
      };

      // Privacy link
      var privacyLink = this.createLink(
        BOTME_BASE_URL + "/legal/end-user-privacy.html",
        "Privacy Policy"
      );

      // Terms link
      var termsLink = this.createLink(
        BOTME_BASE_URL + "/legal/end-user-terms.html",
        "Terms of Use"
      );

      var currentThemeMode = this.getThemeMode();
      var themeToggleLink = this.createActionItem(
        "corner-chat-theme-toggle",
        currentThemeMode === "dark" ? "Switch to light mode" : "Switch to dark mode",
        () => {
          var nextMode = this.getThemeMode() === "dark" ? "light" : "dark";
          this.setThemeMode(nextMode);
        }
      );

      infoPopup.appendChild(popupCloseButton);
      // infoPopup.appendChild(themeToggleLink);
      infoPopup.appendChild(privacyLink);
      infoPopup.appendChild(termsLink);

      // Log button positions for debugging
      this.log('buttons_positioned', {
        dynamicPos: dynamicPos,
        isMobile: isMobile,
        dimensions: dimensions,
        closeButton: {
          id: closeButton.id,
          bottom: closeButton.style.bottom,
          top: closeButton.style.top,
          left: closeButton.style.left,
          right: closeButton.style.right,
          transform: closeButton.style.transform,
          zIndex: closeButton.style.zIndex
        },
        minimizeButton: {
          id: minimizeButton.id,
          bottom: minimizeButton.style.bottom,
          top: minimizeButton.style.top,
          left: minimizeButton.style.left,
          right: minimizeButton.style.right
        },
        infoButton: {
          id: infoButton.id,
          bottom: infoButton.style.bottom,
          top: infoButton.style.top,
          left: infoButton.style.left,
          right: infoButton.style.right
        }
      });

      // Add elements to DOM
      document.body.appendChild(overlay);
      document.body.appendChild(iframe);
      document.body.appendChild(infoOverlay);
      document.body.appendChild(infoPopup);
      document.body.appendChild(infoButton);
      document.body.appendChild(minimizeButton);
      document.body.appendChild(closeButton);
      this.createPopupResizeHandles();

      // New: position the buttons based on iframe geometry
      this.updateButtonPositions();

      // Keep buttons in sync on resize/scroll
      if (!this.updateButtonsBound) {
        this.updateButtonsBound = () => this.updateButtonPositions();
      }
      window.addEventListener('resize', this.updateButtonsBound);
      window.addEventListener('scroll', this.updateButtonsBound);

      // Hide the FAB button when chat is open
      var fabButton = this.getPluginElement("aiButtonContainer");
      if (fabButton) {
        fabButton.style.display = "none";
      }

      // Trigger animation after a brief delay
      setTimeout(() => {
        if (!this.isOpened || this.isMinimized) {
          return;
        }

        overlay.style.opacity = OPACITY_VISIBLE;

        // Apply appropriate animation based on display mode
        if (isDrawerMode) {
          iframe.style.transform = "translateX(0)"; // Slide to visible position
        } else {
          iframe.style.transform = SCALE_VISIBLE; // Scale to visible
        }

        const sleep = (ms = 0) => new Promise(res => setTimeout(res, ms));
        // Animate buttons after iframe animation completes
        setTimeout(() => {
          if (!this.isOpened || this.isMinimized) {
            return;
          }

          // Update button positions after iframe animation completes
          this.updateButtonPositions();

          // Animate buttons in sequentially: close -> minimize -> info
          closeButton.style.transform = SCALE_VISIBLE;
          setTimeout(() => {
            minimizeButton.style.transform = SCALE_VISIBLE;
            setTimeout(() => {
              infoButton.style.transform = SCALE_VISIBLE;

              this.log('buttons_animated', {
                closeButtonTransform: closeButton.style.transform,
                minimizeButtonTransform: minimizeButton.style.transform,
                infoButtonTransform: infoButton.style.transform
              });
            }, ANIM_BUTTON_STAGGER);
          }, ANIM_BUTTON_STAGGER);

        }, ANIM_SCALE_DURATION);
      }, ANIM_INITIAL_DELAY);

      // Setup escape key listener
      if (!this.config.disableEscapeKey && !this.isListeningToEscapeKeyPress) {
        this.isListeningToEscapeKeyPress = true;
        document.addEventListener("keyup", (e) => {
          if (
            e.key === "Escape" &&
            this.isOpened &&
            document.visibilityState === "visible"
          ) {
            this.minimizeChat();
          }
        });
      }

      this.isOpened = true;
    };

    this.toggleInfoPopup = function () {
      var infoPopup = this.getPluginElement("corner-chat-info-popup");
      var infoOverlay = this.getPluginElement("corner-chat-info-overlay");
      var infoButton = this.getPluginElement("corner-chat-info-button");
      if (!infoPopup || !infoOverlay) return;

      if (this.isInfoPopupOpen) {
        // Close popup
        infoPopup.style.opacity = OPACITY_HIDDEN;
        infoPopup.style.transform = SCALE_HIDDEN;
        infoPopup.style.pointerEvents = "none";
        infoOverlay.style.display = "none";
        this.isInfoPopupOpen = false;
      } else {
        // Open popup and log positions
        infoPopup.style.opacity = OPACITY_VISIBLE;
        infoPopup.style.transform = SCALE_VISIBLE;
        infoPopup.style.pointerEvents = "auto";
        infoOverlay.style.display = "block";
        this.isInfoPopupOpen = true;

        this.log('info_popup_opened', {
          buttonStyles: {
            top: infoButton ? infoButton.style.top : null,
            bottom: infoButton ? infoButton.style.bottom : null,
            transform: infoButton ? infoButton.style.transform : null
          },
          popupStyles: {
            top: infoPopup.style.top,
            bottom: infoPopup.style.bottom,
            right: infoPopup.style.right,
            left: infoPopup.style.left
          }
        });
      }
    };

    this.closeInfoPopup = function () {
      var infoPopup = this.getPluginElement("corner-chat-info-popup");
      var infoOverlay = this.getPluginElement("corner-chat-info-overlay");
      if (!infoPopup || !this.isInfoPopupOpen) return;

      infoPopup.style.opacity = OPACITY_HIDDEN;
      infoPopup.style.transform = SCALE_HIDDEN;
      infoPopup.style.pointerEvents = "none";
      if (infoOverlay) {
        infoOverlay.style.display = "none";
      }
      this.isInfoPopupOpen = false;
    };

    this.minimizeChat = function () {
      if (!this.isOpened || this.isMinimized) {
        return;
      }

      this.endPopupInteraction();
      this.resetPopupDragTilt();

      var iframe = this.getPluginElement("corner-chat-element");
      var overlay = this.getPluginElement("corner-chat-overlay");
      var closeButton = this.getPluginElement("corner-chat-close-button");
      var minimizeButton = this.getPluginElement("corner-chat-minimize-button");
      var infoButton = this.getPluginElement("corner-chat-info-button");

      // Determine display mode for animation
      const displayMode = this.lastKnownDisplayMode || this.config.displayMode;
      var isDrawerMode = displayMode === "drawer-left" || displayMode === "drawer-right";
      var isDrawerLeft = displayMode === "drawer-left";

      // Close info popup if open
      this.closeInfoPopup();

      // Animate out (minimize)
      if (iframe) {
        if (isDrawerMode) {
          // Slide out for drawer mode
          iframe.style.transform = isDrawerLeft ? "translateX(-100%)" : "translateX(100%)";
        } else {
          // Scale out for modal mode
          iframe.style.transform = SCALE_HIDDEN;
        }
      }
      if (overlay) {
        overlay.style.opacity = OPACITY_HIDDEN;
        overlay.style.pointerEvents = "none";
      }
      if (closeButton) {
        closeButton.style.transform = SCALE_HIDDEN;
      }
      if (minimizeButton) {
        minimizeButton.style.transform = SCALE_HIDDEN;
      }
      if (infoButton) {
        infoButton.style.transform = SCALE_HIDDEN;
      }

      this.isMinimized = true;
      this.updateButtonPositions();

      // Show the FAB button again
      var fabButton = this.getPluginElement("aiButtonContainer");
      if (fabButton) {
        fabButton.style.display = "block";
      }
    };

    this.restoreChat = function () {
      if (!this.isOpened || !this.isMinimized) {
        return;
      }

      var iframe = this.getPluginElement("corner-chat-element");
      var overlay = this.getPluginElement("corner-chat-overlay");
      var closeButton = this.getPluginElement("corner-chat-close-button");
      var minimizeButton = this.getPluginElement("corner-chat-minimize-button");
      var infoButton = this.getPluginElement("corner-chat-info-button");

      // Determine display mode for animation
      const displayMode = this.lastKnownDisplayMode || this.config.displayMode;
      var isDrawerMode = displayMode === "drawer-left" || displayMode === "drawer-right";

      // Animate in (restore)
      if (iframe) {
        if (isDrawerMode) {
          // Slide in for drawer mode
          iframe.style.transform = "translateX(0)";
        } else {
          // Scale in for modal mode
          iframe.style.transform = SCALE_VISIBLE;
        }
      }
      if (overlay) {
        overlay.style.opacity = OPACITY_VISIBLE;
        overlay.style.pointerEvents = "auto";
      }

      // Update button positions and animate sequentially after iframe animation
      setTimeout(() => {
        if (!this.isOpened || this.isMinimized) {
          return;
        }

        this.updateButtonPositions();

        // Animate buttons in sequentially: close -> minimize -> info
        if (closeButton) {
          closeButton.style.transform = SCALE_VISIBLE;
        }
        setTimeout(() => {
          if (minimizeButton) {
            minimizeButton.style.transform = SCALE_VISIBLE;
          }
          setTimeout(() => {
            if (infoButton) {
              infoButton.style.transform = SCALE_VISIBLE;
            }
          }, ANIM_BUTTON_STAGGER);
        }, ANIM_BUTTON_STAGGER);
      }, ANIM_SCALE_DURATION);

      this.isMinimized = false;

      // Hide the FAB button again
      var fabButton = this.getPluginElement("aiButtonContainer");
      if (fabButton) {
        fabButton.style.display = "none";
      }
    };

    this.closeChat = function () {
      this.closeImageViewer();
      this.imageViewerProbe = null;
      this.endPopupInteraction();
      this.resetPopupDragTilt();
      const resizeHandles = this.getPluginElement("corner-chat-resize-handles");
      if (resizeHandles) resizeHandles.remove();
      this.closeInfoPopup();
      var iframe = this.getPluginElement("corner-chat-element");
      var overlay = this.getPluginElement("corner-chat-overlay");
      var closeButton = this.getPluginElement("corner-chat-close-button");
      var minimizeButton = this.getPluginElement("corner-chat-minimize-button");
      var infoButton = this.getPluginElement("corner-chat-info-button");
      var infoPopup = this.getPluginElement("corner-chat-info-popup");
      var infoOverlay = this.getPluginElement("corner-chat-info-overlay");

      // Determine display mode for animation
      const displayMode = this.lastKnownDisplayMode || this.config.displayMode;
      var isDrawerMode = displayMode === "drawer-left" || displayMode === "drawer-right";
      var isDrawerLeft = displayMode === "drawer-left";
      this.lastKnownDisplayMode = undefined;

      // Animate out
      if (iframe) {
        if (isDrawerMode) {
          // Slide out for drawer mode
          iframe.style.transform = isDrawerLeft ? "translateX(-100%)" : "translateX(100%)";
        } else {
          // Scale out for modal mode
          iframe.style.transform = SCALE_HIDDEN;
        }
      }
      if (overlay) {
        overlay.style.opacity = OPACITY_HIDDEN;
        overlay.style.pointerEvents = "none";
      }
      if (closeButton) {
        closeButton.style.transform = SCALE_HIDDEN;
      }
      if (minimizeButton) {
        minimizeButton.style.transform = SCALE_HIDDEN;
      }
      if (infoButton) {
        infoButton.style.transform = SCALE_HIDDEN;
      }

      if (this.updateButtonsBound) {
        window.removeEventListener('resize', this.updateButtonsBound);
        window.removeEventListener('scroll', this.updateButtonsBound);
      }

      // Remove elements after animation
      setTimeout(() => {
        if (iframe && iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
        this.pendingIframeContext = null;
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
        if (closeButton && closeButton.parentNode) {
          closeButton.parentNode.removeChild(closeButton);
        }
        if (minimizeButton && minimizeButton.parentNode) {
          minimizeButton.parentNode.removeChild(minimizeButton);
        }
        if (infoButton && infoButton.parentNode) {
          infoButton.parentNode.removeChild(infoButton);
        }
        if (infoPopup && infoPopup.parentNode) {
          infoPopup.parentNode.removeChild(infoPopup);
        }
        if (infoOverlay && infoOverlay.parentNode) {
          infoOverlay.parentNode.removeChild(infoOverlay);
        }
      }, ANIM_SCALE_DURATION);

      this.isOpened = false;
      this.isMinimized = false;
      this.isInfoPopupOpen = false;
      this.usedDynamicPositioning = false; // Reset for next session

      // Clear cached coordinates so next open uses new position
      this.lastClickX = null;
      this.lastClickY = null;

      // Show the FAB button again
      var fabButton = this.getPluginElement("aiButtonContainer");
      if (fabButton) {
        fabButton.style.display = "block";
      }
    };

    this.toggleChat = function (botId, event, options) {
      // TODO: Use 'displayMode' to decide which animation will be used.
      this.lastKnownDisplayMode = options?.displayMode;
      // Store mouse coordinates if event is provided
      if (event && event.clientX !== undefined && event.clientY !== undefined) {
        this.lastClickX = event.clientX;
        this.lastClickY = event.clientY;
        this.log('click_coordinates_captured', {
          coordinates: { x: this.lastClickX, y: this.lastClickY }
        });
      }

      if (this.isOpened && !this.isMinimized) {
        this.minimizeChat();
      } else if (this.isOpened && this.isMinimized) {
        this.restoreChat();
      } else {
        this.openChat(botId || options?.botId || options?.apiKey, this.lastClickX, this.lastClickY);
      }
    };

    // Public API method to programmatically open chat
    this.open = function (botId, mouseX, mouseY) {
      this.openChat(botId, mouseX, mouseY);
    };

    // Public API method to programmatically close chat
    this.close = function () {
      this.closeChat();
    };

    // Public API method to toggle chat
    this.toggle = function (botId, event, options) {
      this.toggleChat(botId, event, options);
    };

    // Debug logs API
    this.getDebugLogs = function () {
      return this.debugLogs;
    };

    this.clearDebugLogs = function () {
      this.debugLogs = [];
    };

    this.printDebugLogs = function () {
      this.log('printDebugLogs', {
        level: 'info',
        message: 'BotMePlugin Debug Logs requested',
        debugLogCount: this.debugLogs.length
      });
      return this.debugLogs;
    };

    this.enableDebug = function () {
      this.debugEnabled = true;
    };

    this.disableDebug = function () {
      this.debugEnabled = false;
    };

    // Auto-open if configured
    if (this.config.autoOpen && this.getConfiguredBotId()) {
      setTimeout(() => {
        this.openChat(this.getConfiguredBotId());
      }, ANIM_AUTO_OPEN_DELAY);
    }
  }

  // Expose BotMePlugin to global scope
  window.BotMePlugin = BotMePlugin;

  // Initialize the plugin with default config (for backward compatibility)
  // Users can also create their own instance with custom config
  var botMePlugin = new BotMePlugin();
  setTimeout(() => {
    botMePlugin.generateAndAddFab();
    setTimeout(() => {
      botMePlugin.attachToCustomTriggers();
    }, ATTACH_TRIGGERS_DELAY);
  }, ANIM_FAB_INIT_DELAY);

  // Expose default instance to global scope
  window.botMePlugin = botMePlugin;
})();
